import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useLanguage } from './context/LanguageContext';
import Navbar from './components/Navbar';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopButton from './components/ScrollToTopButton';
import { captureUtmAttribution } from './utils/utmAttribution';
import { trackEvent } from './utils/analytics';

import Home from './pages/Home';

// Keep route-level code splitting while allowing the current route to be loaded
// before hydrating react-snap's HTML.
const createPreloadableRoute = (loader) => {
  const LazyComponent = lazy(loader);
  let LoadedComponent = null;
  let loadingPromise = null;

  const RouteComponent = (props) => (
    LoadedComponent ? <LoadedComponent {...props} /> : <LazyComponent {...props} />
  );

  RouteComponent.preload = () => {
    if (!loadingPromise) {
      loadingPromise = loader().then((module) => {
        LoadedComponent = module.default;
        return module;
      });
    }
    return loadingPromise;
  };

  RouteComponent.isLoaded = () => LoadedComponent !== null;
  return RouteComponent;
};

/**
 * GtmPageTracker — fires one custom virtual pageview after each client-side
 * route change. GTM should trigger SPA page-view tags from this custom event.
 *
 * Lazy-route navigations wait for react-helmet to update document.title;
 * routes with an unchanged title use a short fallback timer.
 */
const GtmPageTracker = () => {
  const location = useLocation();
  const prevRef = useRef(null);

  useEffect(() => {
    const pagePath = location.pathname + location.search + location.hash;
    const previousPage = prevRef.current;
    if (previousPage?.path === pagePath) return; // de-duplicate

    const oldUrl = previousPage
      ? previousPage.location
      : document.referrer || '';
    const oldTitle = previousPage?.title || document.title;

    prevRef.current = {
      path: pagePath,
      location: window.location.href,
      title: document.title,
    };

    let sent = false;
    const pushPageView = () => {
      if (sent || prevRef.current?.path !== pagePath) return;
      sent = true;
      observer.disconnect();
      window.clearTimeout(timerId);

      prevRef.current.title = document.title;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'virtual_page_view',
        page_location: window.location.href,
        previous_page_location: oldUrl,
        page_path: pagePath,
        page_title: document.title,
      });

      // GA4 is configured with send_page_view:false because this is an SPA.
      // Send one explicit pageview for each client-side route transition.
      trackEvent('page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: pagePath,
      });
    };

    // Lazy routes can update their Helmet title after the URL changes. Wait
    // for that mutation, with a fallback for routes that share the same title.
    const observer = new MutationObserver(() => {
      if (document.title !== oldTitle) pushPageView();
    });
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });

    const timerId = window.setTimeout(pushPageView, previousPage ? 1000 : 0);

    return () => {
      observer.disconnect();
      window.clearTimeout(timerId);
    };
  }, [location.pathname, location.search, location.hash]);

  return null;
};

const UtmAttributionTracker = () => {
  const { search } = useLocation();

  useEffect(() => {
    captureUtmAttribution(search);
  }, [search]);

  return null;
};

const About = createPreloadableRoute(() => import('./pages/About'));
const Platform = createPreloadableRoute(() => import('./pages/Platform'));
const AccountManagement = createPreloadableRoute(() => import('./pages/AccountManagement'));
const AccountSetup = createPreloadableRoute(() => import('./pages/AccountSetup'));
const SolutionsPage = createPreloadableRoute(() => import('./pages/Solutions'));
const PricingPage = createPreloadableRoute(() => import('./pages/Pricing'));
const PartnersPage = createPreloadableRoute(() => import('./pages/Partners'));
const AdminPartners = createPreloadableRoute(() => import('./pages/AdminPartners'));
const SupportPage = createPreloadableRoute(() => import('./pages/Support'));
const PrivacyPolicy = createPreloadableRoute(() => import('./pages/PrivacyPolicy'));
const Login = createPreloadableRoute(() => import('./pages/Login'));
const Register = createPreloadableRoute(() => import('./pages/Register'));
const Demo = createPreloadableRoute(() => import('./pages/Demo'));
const MarketingRoute = createPreloadableRoute(() => import('./pages/MarketingRoute'));
const SupportedMarketplaces = createPreloadableRoute(() => import('./pages/SupportedMarketplaces'));
const NotFound = createPreloadableRoute(() => import('./pages/NotFound'));

const routeComponents = {
  '/about': About,
  '/about-platform': Platform,
  '/platform': Platform,
  '/services': AccountManagement,
  '/service/platform': Platform,
  '/service/account-management': AccountManagement,
  '/service/account-setup': AccountSetup,
  '/solutions': SolutionsPage,
  '/pricing': PricingPage,
  '/partners': PartnersPage,
  '/admin/partners': AdminPartners,
  '/support': SupportPage,
  '/supported-marketplaces': SupportedMarketplaces,
  '/privacy': PrivacyPolicy,
  '/login': Login,
  '/register': Register,
  '/demo': Demo,
  '/route': MarketingRoute,
};

export const preloadRoute = (pathname) => {
  const normalizedPathname = pathname.length > 1
    ? pathname.replace(/\/+$/, '')
    : pathname;
  const RouteComponent = routeComponents[normalizedPathname];

  if (normalizedPathname === '/') return Promise.resolve();
  return (RouteComponent || NotFound).preload();
};

const renderLazyPage = (Component) => Component.isLoaded()
  ? <Component />
  : (
      <Suspense fallback={<main className="min-h-screen" aria-busy="true" />}>
        <Component />
      </Suspense>
    );

function AppContent() {
  const { direction } = useLanguage();
  const location = useLocation();
  const isMarketingRoute = location.pathname === '/route';

  return (
    <div className="App" dir={direction}>
        <UtmAttributionTracker />
        <GtmPageTracker />
        {!isMarketingRoute && <CookieConsent />}
        <ScrollToTop />
        <Navbar minimal={isMarketingRoute} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={renderLazyPage(About)} />
          <Route path="/platform" element={renderLazyPage(Platform)} />
          <Route path="/about-platform" element={renderLazyPage(Platform)} />
          <Route path="/services" element={renderLazyPage(AccountManagement)} />
          <Route path="/service/platform" element={renderLazyPage(Platform)} />
          <Route path="/service/account-management" element={renderLazyPage(AccountManagement)} />
          <Route path="/service/account-setup" element={renderLazyPage(AccountSetup)} />
          <Route path="/solutions" element={renderLazyPage(SolutionsPage)} />
          <Route path="/pricing" element={renderLazyPage(PricingPage)} />
          <Route path="/partners" element={renderLazyPage(PartnersPage)} />
          <Route path="/admin/partners" element={renderLazyPage(AdminPartners)} />
          <Route path="/support" element={renderLazyPage(SupportPage)} />
          <Route path="/supported-marketplaces" element={renderLazyPage(SupportedMarketplaces)} />
          <Route path="/privacy" element={renderLazyPage(PrivacyPolicy)} />
          <Route path="/login" element={renderLazyPage(Login)} />
          <Route path="/register" element={renderLazyPage(Register)} />
          <Route path="/demo" element={renderLazyPage(Demo)} />
          <Route path="/route" element={renderLazyPage(MarketingRoute)} />
          <Route path="*" element={renderLazyPage(NotFound)} />
        </Routes>
        {!isMarketingRoute && <ScrollToTopButton />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
